import { create } from "zustand";
import { persist } from "zustand/middleware";
import { CACHE_KEY } from "constants/general";
import { queryClient } from "core/queryClient";

const OPTIMISTIC_UPDATE_TTL_MS = 10 * 60 * 1000;

/**
 * User Info Store
 * Replaces: UserInfoState from recLib/atoms/profile.ts
 *
 * Persisted to localStorage to survive page refreshes.
 */

type UserDataType = {
  accountId?: any;
  firstName?: any;
  lastName?: any;
  image_path?: any;
  [key: string]: any;
};

type UserInfoStoreType = {
  /** Logged-in user profile data */
  userInfo: UserDataType | null;
  /** Epoch ms until optimistic local profile data should override API response */
  optimisticUpdatedUntil: number;

  /** Actions */
  setUserInfo: (data: UserDataType | null) => void;
  setOptimisticUpdatedUntil: () => void;
  resetUserInfo: () => void;
};

export const useUserInfoStore = create<UserInfoStoreType>()(
  persist(
    (set) => ({
      userInfo: null,
      optimisticUpdatedUntil: 0,
      setUserInfo: (data) => {
        set({ userInfo: data });
        queryClient.setQueryData(
          [CACHE_KEY.PROFILE_QUERY_KEYS.PROFILE_DETAILS],
          data,
        );
      },
      setOptimisticUpdatedUntil: () =>
        set({ optimisticUpdatedUntil: Date.now() + OPTIMISTIC_UPDATE_TTL_MS }),
      resetUserInfo: () => {
        set({ userInfo: null, optimisticUpdatedUntil: 0 });
        queryClient.removeQueries({
          queryKey: [CACHE_KEY.PROFILE_QUERY_KEYS.PROFILE_DETAILS],
        });
      },
    }),
    {
      name: "user-info-storage",
    },
  ),
);
